import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import axios from 'axios';
import { useTheme } from '../App';

/* ─── Particle canvas ────────────────────────────────── */
function ParticleCanvas() {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext('2d'); let raf;
    const pts = Array.from({ length: 55 }, () => ({
      x: Math.random(), y: Math.random(),
      vx: (Math.random() - 0.5) * 0.00025, vy: (Math.random() - 0.5) * 0.00025,
      r: Math.random() * 1.1 + 0.3, a: Math.random() * 0.35 + 0.08, phase: Math.random() * Math.PI * 2,
    }));
    let t = 0;
    const resize = () => { c.width = c.offsetWidth; c.height = c.offsetHeight; };
    resize(); window.addEventListener('resize', resize);
    const draw = () => {
      c.width = c.offsetWidth; c.height = c.offsetHeight;
      const W = c.width, H = c.height; ctx.clearRect(0, 0, W, H);
      pts.forEach(p => {
        p.x = (p.x + p.vx + 1) % 1; p.y = (p.y + p.vy + 1) % 1;
        const a = p.a + Math.sin(t * 0.018 + p.phase) * 0.06;
        ctx.beginPath(); ctx.arc(p.x * W, p.y * H, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0,240,255,${Math.max(0.04, a)})`; ctx.fill();
      });
      for (let i = 0; i < pts.length; i++) for (let j = i + 1; j < pts.length; j++) {
        const dx = (pts[i].x - pts[j].x) * W, dy = (pts[i].y - pts[j].y) * H, d = Math.hypot(dx, dy);
        if (d < 130) {
          ctx.beginPath(); ctx.moveTo(pts[i].x * W, pts[i].y * H); ctx.lineTo(pts[j].x * W, pts[j].y * H);
          ctx.strokeStyle = `rgba(0,240,255,${(1 - d / 130) * 0.055})`; ctx.lineWidth = 0.6; ctx.stroke();
        }
      }
      t++; raf = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={ref} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }} />;
}

/* ─── Scroll-reveal hook ─────────────────────────────── */
function useScrollReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); observer.disconnect(); } }, { threshold: 0.08 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, visible];
}

const Home = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const isLight = theme !== 'dark';

  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: 'instant' }); }, []);

  useEffect(() => {
    const reqId = axios.interceptors.request.use(cfg => {
      if (cfg.url && cfg.url.includes('/api/')) cfg.url = cfg.url.replace('/api/', '/');
      return cfg;
    });
    const resId = axios.interceptors.response.use(res => {
      if (res.data && typeof res.data.response === 'string') {
        res.data.response = res.data.response
          .replace(/\*\*(.*?)\*\*/g, '$1')
          .replace(/\*(.*?)\*/g, '$1')
          .replace(/^[-•]\s+/gm, '• ')
          .replace(/^\d+\.\s+/gm, '');
      }
      return res;
    });
    return () => {
      axios.interceptors.request.eject(reqId);
      axios.interceptors.response.eject(resId);
    };
  }, []);

  const [loaded, setLoaded] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  const [inspectionRef, inspectionVis] = useScrollReveal();
  const [valuationRef, valuationVis] = useScrollReveal();

  useEffect(() => { setTimeout(() => setLoaded(true), 80); }, []);
  useEffect(() => {
    const fn = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  const ac = isLight ? '#007A8A' : '#00E5FF';
  const acDeep = isLight ? '#005F6E' : '#00E5FF';
  const textMain = isLight ? '#0A1628' : '#f1f5f9';
  const textMuted = isLight ? 'rgba(10,22,40,0.48)' : 'rgba(241,245,249,0.6)';
  const textFaint = isLight ? 'rgba(10,22,40,0.28)' : 'rgba(255,255,255,0.45)';
  const cardBg = isLight ? '#FFFFFF' : 'rgba(10,15,26,0.62)';
  const cardBorder = isLight ? '1px solid rgba(0,122,138,0.18)' : '1px solid rgba(255,255,255,0.08)';
  const dividerColor = isLight ? 'rgba(10,22,40,0.07)' : 'rgba(255,255,255,0.06)';

  const sectionLabel = text => (
    <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: ac, marginBottom: 16 }}>{text}</div>
  );

  const sectionCta = (label, onClick) => (
    <button onClick={onClick} style={{
      width: '100%', padding: '15px 24px', borderRadius: 50, border: 'none',
      background: ac, color: isLight ? '#fff' : '#080d16',
      fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: '0.88rem',
      cursor: 'pointer', transition: 'all 0.2s ease',
      boxShadow: isLight ? '0 0 24px rgba(0,153,170,0.3)' : '0 0 24px rgba(0,229,255,0.3)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    }}>{label}</button>
  );

  const trustLine = text => (
    <p style={{ fontFamily: "'Manrope',sans-serif", fontSize: '0.62rem', color: isLight ? '#B0ABA6' : 'rgba(255,255,255,0.3)', textAlign: 'center', marginTop: 10, margin: '10px 0 0' }}>{text}</p>
  );

  return (
    <div style={{ minHeight: '100vh', background: isLight ? `#EEF6F8 url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3Cpath d='M 40 0 L 0 0 0 40' fill='none' stroke='rgba(0%2C122%2C138%2C0.055)' stroke-width='1'/%3E%3C/svg%3E")` : '#080d16', backgroundAttachment: isLight ? 'fixed' : undefined, color: isLight ? '#0A1628' : '#fff', fontFamily: "'Sora',sans-serif", overflowX: 'hidden', paddingBottom: 100 }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@200;300;400;500;600;700;800&family=Manrope:wght@400;500;600;700&display=swap');
        @keyframes fu  { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }
        @keyframes fi  { from{opacity:0} to{opacity:1} }
        @keyframes live-ring   { 0%{transform:scale(1);opacity:.7} 100%{transform:scale(2.4);opacity:0} }
        @keyframes scanline    { 0%{top:-1px} 100%{top:100%} }
        @keyframes pulseRing {
          0%   { transform: translate(-50%, -50%) scale(1);   opacity: 0.75; }
          100% { transform: translate(-50%, -50%) scale(2.6); opacity: 0; }
        }
        .pulse-ring {
          position: absolute;
          width: 220px;
          height: 220px;
          border-radius: 50%;
          top: 50%;
          left: 50%;
          pointer-events: none;
          z-index: 4;
          will-change: transform, opacity;
          animation: pulseRing 2.4s cubic-bezier(0.15, 0.5, 0.4, 1) infinite;
        }
        .pulse-ring-light {
          border: 2px solid rgba(0,122,138,0.65);
          box-shadow: 0 0 18px rgba(0,122,138,0.5), inset 0 0 10px rgba(0,122,138,0.2);
        }
        .pulse-ring-dark {
          border: 2px solid rgba(0,229,255,0.6);
          box-shadow: 0 0 18px rgba(0,229,255,0.5), inset 0 0 10px rgba(0,229,255,0.15);
        }
        .pulse-ring:nth-child(2) { animation-delay: 0s; }
        .pulse-ring:nth-child(3) { animation-delay: 0.8s; }
        .pulse-ring:nth-child(4) { animation-delay: 1.6s; }
        @keyframes chipOrbitL0{0%,100%{transform:translate(0,0)}25%{transform:translate(-6px,-5px)}50%{transform:translate(-3px,6px)}75%{transform:translate(5px,3px)}}
        @keyframes chipOrbitL1{0%,100%{transform:translate(0,0)}25%{transform:translate(5px,-6px)}50%{transform:translate(6px,4px)}75%{transform:translate(-4px,5px)}}
        @keyframes chipOrbitL2{0%,100%{transform:translate(0,0)}25%{transform:translate(-5px,6px)}50%{transform:translate(4px,-4px)}75%{transform:translate(-6px,-3px)}}
        @keyframes chipOrbitR0{0%,100%{transform:translate(0,0)}25%{transform:translate(6px,-4px)}50%{transform:translate(-4px,-6px)}75%{transform:translate(3px,6px)}}
        @keyframes chipOrbitR1{0%,100%{transform:translate(0,0)}25%{transform:translate(-6px,5px)}50%{transform:translate(5px,6px)}75%{transform:translate(6px,-5px)}}
        @keyframes chipOrbitR2{0%,100%{transform:translate(0,0)}25%{transform:translate(4px,6px)}50%{transform:translate(-6px,3px)}75%{transform:translate(3px,-6px)}}
        @keyframes skP         { 0%,100%{opacity:.4} 50%{opacity:.85} }
        @keyframes scrollBounce{ 0%,100%{transform:translateY(0)} 50%{transform:translateY(5px)} }
        .u1{animation:fu .7s cubic-bezier(.22,1,.36,1) both .05s}
        .u2{animation:fu .7s cubic-bezier(.22,1,.36,1) both .18s}
        .u3{animation:fu .7s cubic-bezier(.22,1,.36,1) both .30s}
        .u4{animation:fu .7s cubic-bezier(.22,1,.36,1) both .42s}
        .sec-reveal{opacity:0;transform:translateY(28px);transition:opacity .7s cubic-bezier(.22,1,.36,1),transform .7s cubic-bezier(.22,1,.36,1)}
        .sec-reveal.vis{opacity:1;transform:none}

        /* Nav */
        .pq-nav{position:fixed;top:0;left:0;right:0;z-index:200;height:58px;display:flex;align-items:center;transition:background .4s,border-color .4s,backdrop-filter .4s}
        .pq-nw{max-width:1200px;margin:0 auto;padding:0 40px;width:100%;display:flex;align-items:center;justify-content:space-between;gap:24px}
        .pq-logo{display:flex;align-items:center;gap:4px;flex-shrink:0}
        .pq-logo-sq{width:28px;height:28px;display:flex;align-items:center;justify-content:center;flex-shrink:0;margin-right:5px}
        .pq-logo-name{font-family:'Sora',sans-serif;font-weight:700;font-size:.95rem;letter-spacing:-.01em}

        /* Hero */
        .pq-scan{position:absolute;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,rgba(0,229,255,.15),transparent);animation:scanline 8s linear infinite;pointer-events:none;opacity:.6;z-index:2}

        /* Chips */
        .pq-chip{transition:all .2s ease}
        .pq-chip:hover{transform:translateY(-2px) scale(1.02)}

        /* Orbital rings — hidden on mobile */
        .pq-orbital-rings{display:block}

        /* Scroll indicator */
        .foot-social{width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;transition:all .2s ease;text-decoration:none}

        /* Mobile */
        @media(max-width:768px){
          .pq-nw{padding:0 20px}
        }
        @media(max-width:640px){
          .pq-orbital-rings{display:none}
          .pq-chips-left,.pq-chips-right{display:none}
          .pq-ncta{font-size:.72rem;padding:8px 16px}
        }

        ${isLight ? `
          .pq-logo-name{color:#0A1628!important}
          .foot-social{border:1px solid rgba(0,122,138,0.12)!important}
          .foot-social:hover{border-color:rgba(0,122,138,0.35)!important}
        ` : `
          .foot-social{border:1px solid rgba(255,255,255,0.1)!important}
          .foot-social:hover{border-color:rgba(0,229,255,0.3)!important}
        `}
      `}</style>

      {/* ── NAV ── */}
      <nav
        className="pq-nav"
        style={{
          background: scrollY > 40
            ? (isLight ? 'rgba(238,246,248,0.97)' : 'rgba(6,18,24,0.94)')
            : (isLight ? 'rgba(238,246,248,0.55)' : 'transparent'),
          borderBottom: scrollY > 40
            ? (isLight ? '1px solid rgba(0,122,138,0.10)' : '1px solid rgba(255,255,255,0.05)')
            : '1px solid transparent',
          backdropFilter: scrollY > 40 ? 'blur(22px)' : 'none',
        }}
      >
        <div className="pq-nw">
          <div className="pq-logo">
            <div className="pq-logo-sq">
              <img src="/logo.png" alt="Propspector logo" style={{ width: 26, height: 26, objectFit: 'contain', display: 'block' }} />
            </div>
            <span className="pq-logo-name" style={{ color: isLight ? '#0A1628' : '#fff' }}>Propspector</span>
          </div>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="pq-hero" style={{ background: isLight ? 'linear-gradient(180deg, #E2F2F6 0%, #EEF6F8 55%, #EEF6F8 100%)' : '#061218', minHeight: '100vh', position: 'relative', display: 'flex', flexDirection: 'column', overflow: 'visible' }}>
        <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', zIndex: 0, pointerEvents: 'none' }}>
          {!isLight && <ParticleCanvas />}
          {!isLight && <div className="pq-scan" />}
        </div>

        {/* Deep teal radial glow behind orbital */}
        {!isLight && (
          <div style={{
            position: 'absolute',
            bottom: 0, left: '50%',
            transform: 'translateX(-50%)',
            width: 700, height: 500,
            background: 'radial-gradient(ellipse at center bottom, rgba(0,180,200,0.13) 0%, rgba(0,100,120,0.07) 40%, transparent 70%)',
            pointerEvents: 'none',
            zIndex: 1,
          }} />
        )}

        {isLight && (
          <div style={{
            position: 'absolute',
            bottom: 0, left: '50%',
            transform: 'translateX(-50%)',
            width: 600, height: 400,
            background: 'radial-gradient(ellipse at center bottom, rgba(0,150,160,0.10) 0%, rgba(0,122,138,0.04) 45%, transparent 70%)',
            pointerEvents: 'none',
            zIndex: 1,
          }} />
        )}

        <div style={{ position: 'relative', zIndex: 5, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '100px 24px 0', textAlign: 'center' }}>

          {/* Badge */}
          <div className={loaded ? 'u1' : ''} style={{ opacity: loaded ? undefined : 0 }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              border: isLight ? '1px solid rgba(0,122,138,0.20)' : '1px solid rgba(255,255,255,0.12)',
              borderRadius: 50, padding: '7px 18px', marginBottom: 32,
            }}>
              <div style={{ position: 'relative', width: 6, height: 6, flexShrink: 0 }}>
                <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: isLight ? '#007A8A' : '#00E5FF' }} />
                <div style={{ position: 'absolute', inset: -2, borderRadius: '50%', border: isLight ? '1px solid rgba(0,122,138,0.5)' : '1px solid rgba(0,229,255,0.4)', animation: 'live-ring 2.5s ease-out infinite' }} />
              </div>
              <span style={{
                fontFamily: "'Manrope',sans-serif",
                fontSize: '0.62rem',
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                fontWeight: 500,
                color: isLight ? 'rgba(10,22,40,0.42)' : 'rgba(255,255,255,0.38)',
              }}>Bangalore · Real Estate Intelligence</span>
            </div>
          </div>

          {/* Headline */}
          <div className={loaded ? 'u2' : ''} style={{ opacity: loaded ? undefined : 0 }}>
            <h1 style={{
              fontFamily: "'Sora',sans-serif",
              fontSize: 'clamp(3.2rem, 7vw, 6rem)',
              lineHeight: 1.0,
              letterSpacing: '-0.05em',
              margin: '0 0 20px',
              padding: 0,
            }}>
              <span style={{
                display: 'block',
                fontWeight: 300,
                fontSize: '0.78em',
                color: isLight ? 'rgba(10,22,40,0.22)' : 'rgba(255,255,255,0.38)',
              }}>Buy with</span>
              {isLight ? (
                <span style={{
                  display: 'block',
                  fontWeight: 800,
                  fontStyle: 'normal',
                  color: '#007A8A',
                  letterSpacing: '-0.055em',
                  lineHeight: 1.0,
                }}>conviction.</span>
              ) : (
                <span style={{
                  display: 'block',
                  fontWeight: 800,
                  color: '#00E5FF',
                  letterSpacing: '-0.055em',
                }}>conviction.</span>
              )}
            </h1>
          </div>

          {/* Subtitle */}
          <div className={loaded ? 'u3' : ''} style={{ opacity: loaded ? undefined : 0 }}>
            <p style={{
              fontFamily: "'Manrope',sans-serif",
              fontSize: '0.88rem',
              color: isLight ? 'rgba(10,22,40,0.45)' : 'rgba(255,255,255,0.42)',
              lineHeight: 1.6,
              margin: '0 0 28px',
              fontWeight: 400,
            }}>
              Honest valuations and expert inspections for Bangalore homes.
            </p>
          </div>

          {/* Buttons */}
          <div className={loaded ? 'u4' : ''} style={{ opacity: loaded ? undefined : 0, display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', marginBottom: 0 }}>
            <button
              onClick={() => navigate('/valuate')}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: isLight ? '#007A8A' : '#00E5FF',
                color: isLight ? '#FFFFFF' : '#061218',
                fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: '0.85rem',
                padding: '13px 28px', borderRadius: 50, border: 'none',
                cursor: 'pointer', transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = isLight ? '0 8px 24px rgba(0,122,138,0.35)' : '0 8px 24px rgba(0,229,255,0.3)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
            >
              Valuate a property <ChevronRight size={14} />
            </button>
            <button
              onClick={() => navigate('/inspect')}
              data-testid="hero-cta-button"
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                background: 'transparent',
                color: isLight ? '#007A8A' : 'rgba(255,255,255,0.65)',
                fontFamily: "'Sora',sans-serif", fontWeight: 600, fontSize: '0.85rem',
                padding: '12px 28px', borderRadius: 50,
                border: isLight ? '1.5px solid rgba(0,122,138,0.35)' : '1.5px solid rgba(255,255,255,0.18)',
                cursor: 'pointer', transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = isLight ? 'rgba(0,122,138,0.6)' : 'rgba(0,229,255,0.4)'; e.currentTarget.style.color = isLight ? '#005F6E' : '#00E5FF'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = isLight ? 'rgba(0,122,138,0.35)' : 'rgba(255,255,255,0.18)'; e.currentTarget.style.color = isLight ? '#007A8A' : 'rgba(255,255,255,0.65)'; }}
            >
              Book Inspection · ₹7,999
            </button>
          </div>
        </div>

        {/* Scroll indicator */}
        <div
          onClick={() => document.querySelector('section:nth-of-type(2)')?.scrollIntoView({ behavior: 'smooth' })}
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, cursor: 'pointer', paddingBottom: 28, zIndex: 5, position: 'relative' }}
        >
          <span style={{ fontFamily: "'Manrope',sans-serif", fontSize: '0.55rem', color: isLight ? 'rgba(10,22,40,0.30)' : 'rgba(255,255,255,0.28)', letterSpacing: '0.22em', textTransform: 'uppercase' }}>Scroll to explore</span>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={isLight ? 'rgba(10,22,40,0.30)' : 'rgba(255,255,255,0.28)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'scrollBounce 2s ease-in-out infinite' }}><polyline points="6 9 12 15 18 9"/></svg>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          SECTION 1 — INSPECTION
      ═══════════════════════════════════════════════════════════════════════ */}
      <section ref={inspectionRef} style={{ background: isLight ? '#EEF6F8' : '#080d16', padding: '60px 20px', borderTop: `1px solid ${dividerColor}` }}>
        <div className={`sec-reveal ${inspectionVis ? 'vis' : ''}`} style={{ maxWidth: 480, margin: '0 auto' }}>
          {sectionLabel('01 — INSPECTION')}
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: '2rem', color: textMain, letterSpacing: '-0.03em', lineHeight: 1.1, margin: '0 0 4px' }}>
            Don't buy
          </h2>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: '2rem', color: ac, letterSpacing: '-0.03em', lineHeight: 1.1, margin: '0 0 20px' }}>
            blind.
          </h2>
          <p style={{ fontFamily: "'Manrope',sans-serif", fontSize: '0.88rem', color: isLight ? '#6A6560' : 'rgba(255,255,255,0.55)', lineHeight: 1.7, margin: '0 0 32px' }}>
            A certified structural engineer walks the property, photographs every defect, verifies the paperwork, and tells you — buy, negotiate, or pass.
          </p>

          {/* Price display */}
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: '5rem', color: ac, lineHeight: 1, textShadow: isLight ? 'none' : '0 0 40px rgba(0,229,255,0.3)' }}>₹7,999</div>
            <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: isLight ? '#B0ABA6' : 'rgba(255,255,255,0.3)', marginTop: 8 }}>FLAT FEE · NO COMMISSION · NO UPSELL</div>
            <div style={{ fontFamily: "'Manrope',sans-serif", fontSize: '0.62rem', color: isLight ? '#B0ABA6' : 'rgba(255,255,255,0.2)', marginTop: 6 }}>Enforceable if listing withdraws before visit</div>
          </div>

          {/* 4 checkpoints */}
          {[
            { label: 'Physical visit by certified expert', detail: 'STRUCTURAL ENGINEER' },
            { label: 'Structural walkthrough', detail: '40-POINT AUDIT' },
            { label: 'Full report in 72 hours', detail: 'PDF + PHOTOS + VIDEO' },
            { label: 'Honest verdict — buy / negotiate / pass', detail: 'NO CONFLICTS' },
          ].map((cp, i, arr) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: i < arr.length - 1 ? `1px solid ${isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.04)'}` : 'none' }}>
              <div style={{ width: 20, height: 20, borderRadius: '50%', background: isLight ? 'rgba(0,153,170,0.1)' : 'rgba(0,229,255,0.1)', border: isLight ? '1px solid rgba(0,153,170,0.2)' : '1px solid rgba(0,229,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={ac} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
              <span style={{ flex: 1, fontFamily: "'Sora',sans-serif", fontWeight: 600, fontSize: '0.81rem', color: textMain }}>{cp.label}</span>
              <span style={{ fontFamily: "'Manrope',sans-serif", fontWeight: 600, fontSize: '0.62rem', color: isLight ? '#B0ABA6' : 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0 }}>{cp.detail}</span>
            </div>
          ))}

          <div style={{ marginTop: 24 }}>
            {sectionCta('Book an Inspection →', () => navigate('/inspect'))}
            {trustLine('Flat fee · No commission · No upsell')}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          SECTION 2 — VALUATION
      ═══════════════════════════════════════════════════════════════════════ */}
      <section ref={valuationRef} style={{ background: isLight ? '#EEF6F8' : '#080d16', padding: '60px 20px', borderTop: `1px solid ${dividerColor}` }}>
        <div className={`sec-reveal ${valuationVis ? 'vis' : ''}`} style={{ maxWidth: 480, margin: '0 auto' }}>
          {sectionLabel('02 — VALUATION')}
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: '2rem', color: textMain, letterSpacing: '-0.03em', lineHeight: 1.1, margin: '0 0 4px' }}>
            Know the fair price
          </h2>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: '2rem', color: ac, letterSpacing: '-0.03em', lineHeight: 1.1, margin: '0 0 20px' }}>
            before you negotiate.
          </h2>
          <p style={{ fontFamily: "'Manrope',sans-serif", fontSize: '0.88rem', color: isLight ? '#6A6560' : 'rgba(255,255,255,0.55)', lineHeight: 1.7, margin: '0 0 24px' }}>
            AI-powered valuation using real Bangalore market data — locality medians, sublocality benchmarks, and live price signals.
          </p>

          {/* Valuation preview card (static mock) */}
          <div style={{ background: cardBg, backdropFilter: isLight ? 'none' : 'blur(24px)', border: isLight ? '1px solid rgba(0,153,170,0.15)' : '1px solid rgba(0,229,255,0.1)', borderRadius: 16, padding: 20, marginBottom: 24 }}>
            <span style={{ display: 'inline-block', background: isLight ? 'rgba(0,153,170,0.06)' : 'rgba(0,229,255,0.06)', border: isLight ? '1px solid rgba(0,153,170,0.15)' : '1px solid rgba(0,229,255,0.15)', color: isLight ? 'rgba(0,153,170,0.6)' : 'rgba(0,229,255,0.6)', fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: '0.56rem', textTransform: 'uppercase', borderRadius: 50, padding: '3px 10px', marginBottom: 16 }}>Sample Output</span>
            <div style={{ fontFamily: "'Manrope',sans-serif", fontSize: '0.69rem', color: isLight ? '#B0ABA6' : 'rgba(255,255,255,0.35)', marginBottom: 12 }}>3 BHK · HSR Layout · 1,460 sqft</div>
            <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: isLight ? '#B0ABA6' : 'rgba(255,255,255,0.3)', marginBottom: 4 }}>FAIR VALUE RANGE</div>
            <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: '1.12rem', color: ac, marginBottom: 16 }}>₹1.15 Cr — ₹1.24 Cr</div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {[
                { label: 'PRICE/SQFT', value: '₹8,200' },
                { label: 'vs MEDIAN', value: '+4% above' },
                { label: 'CONFIDENCE', value: '68 · capped pending inspection' },
              ].map((chip, i) => (
                <div key={i} style={{ flex: 1, minWidth: 100, background: isLight ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.03)', border: isLight ? '1px solid rgba(0,0,0,0.05)' : '1px solid rgba(255,255,255,0.07)', borderRadius: 10, padding: '10px 12px' }}>
                  <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: isLight ? '#B0ABA6' : 'rgba(255,255,255,0.3)', marginBottom: 4 }}>{chip.label}</div>
                  <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: '0.81rem', color: textMain }}>{chip.value}</div>
                </div>
              ))}
            </div>

            <div style={{ height: 1, background: isLight ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)', margin: '14px 0' }} />

            <div style={{ fontFamily: "'Manrope',sans-serif", fontSize: '0.81rem', color: isLight ? '#6A6560' : 'rgba(255,255,255,0.55)', lineHeight: 1.6, borderLeft: `2px solid ${isLight ? 'rgba(0,153,170,0.3)' : 'rgba(0,229,255,0.3)'}`, paddingLeft: 12, marginTop: 12 }}>
              Priced 4% below the sublocality median for 3BHK in HSR Sector 2. Comparable listings trade at ₹8,400–8,600/sqft.
            </div>
          </div>

          {sectionCta('Valuate a Property →', () => navigate('/valuate'))}
          {trustLine('Takes 2 minutes · Instant result · No login required')}
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          FOOTER
      ═══════════════════════════════════════════════════════════════════════ */}
      <footer style={{
        background: isLight ? 'rgba(0,122,138,0.04)' : 'rgba(0,0,0,0.3)',
        borderTop: isLight ? '1px solid rgba(0,0,0,0.07)' : '1px solid rgba(255,255,255,0.05)',
        padding: '40px 20px 100px',
      }}>
        <div style={{ maxWidth: 680, margin: '0 auto' }}>
          {/* Logo + tagline */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 8 }}>
            <span style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: '1.25rem', color: textMain }}>Propspector</span>
            <span style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: '1.25rem', color: ac }}>.</span>
          </div>
          <p style={{ fontFamily: "'Manrope',sans-serif", fontSize: '0.75rem', color: isLight ? '#9A9490' : 'rgba(255,255,255,0.4)', lineHeight: 1.6, maxWidth: 240, margin: '0 0 32px' }}>
            AI-powered property intelligence for Bangalore. Real data. Honest analysis. ₹7,999 expert inspection.
          </p>

          {/* 2 link columns */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 40, marginBottom: 40 }}>
            <div>
              <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.12em', color: isLight ? '#9A9490' : 'rgba(255,255,255,0.45)', marginBottom: 14 }}>Product</div>
              {[
                { label: 'Inspection', path: '/inspect' },
                { label: 'Valuation', path: '/valuate' },
              ].map(link => (
                <div key={link.path} onClick={() => navigate(link.path)} style={{ fontFamily: "'Manrope',sans-serif", fontSize: '0.82rem', color: isLight ? '#6A6560' : 'rgba(255,255,255,0.55)', marginBottom: 10, cursor: 'pointer', transition: 'color 0.2s ease', display: 'block' }}
                  onMouseEnter={e => e.currentTarget.style.color = ac}
                  onMouseLeave={e => e.currentTarget.style.color = isLight ? '#6A6560' : 'rgba(255,255,255,0.55)'}
                >{link.label}</div>
              ))}
            </div>
            <div>
              <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.12em', color: isLight ? '#9A9490' : 'rgba(255,255,255,0.45)', marginBottom: 14 }}>Company</div>
              {[
                { label: 'Charter', href: '#' },
                { label: 'Privacy', href: '#' },
                { label: 'Terms', href: '#' },
                { label: 'Contact', href: 'mailto:muadhmalik251@gmail.com' },
              ].map(link => (
                <a key={link.label} href={link.href} style={{ fontFamily: "'Manrope',sans-serif", fontSize: '0.82rem', color: isLight ? '#6A6560' : 'rgba(255,255,255,0.55)', marginBottom: 10, textDecoration: 'none', transition: 'color 0.2s ease', display: 'block' }}
                  onMouseEnter={e => e.currentTarget.style.color = ac}
                  onMouseLeave={e => e.currentTarget.style.color = isLight ? '#6A6560' : 'rgba(255,255,255,0.55)'}
                >{link.label}</a>
              ))}
            </div>
          </div>

          {/* Bottom bar */}
          <div style={{ paddingTop: 20, borderTop: isLight ? '1px solid rgba(0,0,0,0.05)' : '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <span style={{ fontFamily: "'Manrope',sans-serif", fontSize: '0.62rem', color: isLight ? '#B0ABA6' : 'rgba(255,255,255,0.25)' }}>© 2026 — Perspective Propview Pvt Ltd · Bangalore</span>
            <div style={{ display: 'flex', gap: 12 }}>
              <a className="foot-social" href="https://instagram.com/prspective.ig" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={isLight ? '#9A9490' : 'rgba(255,255,255,0.4)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="5"/><circle cx="17.5" cy="6.5" r="1.5" fill={isLight ? '#9A9490' : 'rgba(255,255,255,0.4)'} stroke="none"/></svg>
              </a>
              <a className="foot-social foot-social-wa" href="https://wa.me/918884108992" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">
                <svg width="14" height="14" viewBox="0 0 24 24" fill={isLight ? '#9A9490' : 'rgba(255,255,255,0.4)'}><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.832-1.438A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2z"/></svg>
              </a>
              <a className="foot-social" href="mailto:muadhmalik251@gmail.com" aria-label="Email">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={isLight ? '#9A9490' : 'rgba(255,255,255,0.4)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 7l-10 7L2 7"/></svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
